import { startOfMonth } from 'date-fns';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { toCalendarDay } from '@adornis/datetime/calendarDay.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import type { ValidationFunction, ValidationFunctionOptions } from '@adornis/validation/validator.js';
import type { Person } from '../../users/entities/person.js';
import { EntityClass } from '../enums/class-names.js';

export const formatValidationError = (error: ValidationError<any>) => {
  logger.error({ error }, 'form error');
  let error_msg = 'Ein oder mehrere Felder wurden nicht korrekt ausgefüllt: ';
  for (const validationError of error.extensions.validationErrors) {
    error_msg =
      error_msg +
      `${validationError.message} , Quelle: ${validationError.source?._class}, Pfad: ${[
        ...validationError.extensions?.path,
        validationError.extensions?.key,
      ].join(' => ')}`;
  }

  return error_msg.trim();
};

export const isUser = (entity?: Readonly<any>): entity is Person => {
  return entity?._class === EntityClass.PERSON;
};

// TODO what to do with this? No Idea what Tin even is. Seems to complex to just throw it away
export function validTin(tin: string) {
  // Allow space, minus(-) and slash (/) as number separators
  tin = tin.trim().replace(/ |\/|\-/g, '');

  // 11 digits
  if (!/^[0-9]{11}$/.test(tin)) {
    return false;
  }

  const firstTen = tin.slice(0, 10);

  // Count the number of occurrences of each digit
  const occurrences = firstTen.split('').reduce((acc, d) => acc.set(d, (acc.get(d) || 0) + 1), new Map());

  const keys = [...occurrences.keys()];
  const values = [...occurrences.values()];

  // If one digit occurs twice, the length of keys will be 9
  // If one digit occurs three times OR if two different digits occurs twice
  // each, the length will be 8
  if (keys.length !== 9 && keys.length !== 8) {
    return false;
  }

  if (keys.length === 8) {
    // Check how many times the digit that occurred the most times occurred
    const maxOccurrences = values.reduce((max, num) => Math.max(max, num));

    // If maxOccurrences is 2, we know two different numbers occurred twice
    // each. This is not a valid tin.
    // A final restriction is that a number can not occur three times in a row
    if (maxOccurrences === 2 || /(\d)\1\1/.test(firstTen)) {
      return false;
    }
  }

  // Calculate the checksum digit
  let m11 = 10;
  let m10 = 0;

  for (let i = 0; i < 10; i++) {
    m10 = (parseInt(tin[i]!, 10) + m11) % 10;
    if (m10 === 0) {
      m10 = 10;
    }

    m11 = (2 * m10) % 11;
  }

  let digit = 11 - m11;
  if (digit === 10) {
    digit = 0;
  }
  return digit === parseInt(tin[10]!, 10);
}

export const validationHelper =
  (validation: ValidationFunction<any, any>) => (v: ValidationFunctionOptions<string, any>) => {
    if (!v.person) return true; /** this is used to only validate client-side when wanted */
    return validation(v);
  };

export interface NonOptionalOptions {
  message?: string;
  allowFalsy: boolean;
}

/**
 * These validators run on the server *and* in the browser (the job ticket editor calls them
 * directly), so every reading is done on the canonical UTC grid - date-fns' local-zone helpers would
 * make the same instant validate differently on the two tiers.
 */
const monthStart = (day: Date) => toCalendarDay(startOfMonth(day));

/** Whole calendar months from `from` to `to`. December -> January is exactly 1. */
const monthsBetween = (from: Date, to: Date) =>
  (to.getUTCFullYear() - from.getUTCFullYear()) * 12 + (to.getUTCMonth() - from.getUTCMonth());

/**
 * August
 */
export const validateJobTicketOrderDate = (currentDate: Date, orderDate: Date, key: string) => {
  const current = toCalendarDay(currentDate);
  const order = toCalendarDay(orderDate);
  const monthsTillValidDate = monthsBetween(current, order);

  if (order < current) throw new ValidationError('', { key, translationKey: 'validation_jobticket_past' });

  // selected date is far enough in the future
  if (monthsTillValidDate > 1) return;
  if (monthsTillValidDate === 1 && current.getUTCDate() < 10) return;

  throw new ValidationError('Hallo', { key, translationKey: 'validation_jobticket_order_date' });
};

export const validateJobTicketCancelDate = (
  currentDate: Date,
  orderDate: Maybe<Date>,
  cancelDate: Date,
  key: string,
) => {
  if (!orderDate) throw new ValidationError('', { key, translationKey: 'validation_jobticket_order_missing' });

  const current = toCalendarDay(currentDate);
  const order = monthStart(toCalendarDay(orderDate));
  const cancel = monthStart(toCalendarDay(cancelDate));
  const monthsTillValidDate = monthsBetween(current, cancel);

  if (current > cancel) throw new ValidationError('', { key, translationKey: 'validation_jobticket_cancel_past' });
  if (order > cancel)
    throw new ValidationError('', { key, translationKey: 'validation_jobticket_cancel_before_order' });

  if (monthsTillValidDate > 1) return;
  if (monthsTillValidDate === 1 && current.getUTCDate() < 10) return;

  throw new ValidationError('', { key, translationKey: 'validation_jobticket_cancel_date' });
};
